import type { BlockCardsList, CardList } from 'types/block-cards-list';
import React, { useMemo } from 'react';
import {
  Wrap,
  Button,
  Card,
  Paragraph,
  CardProps,
  Badge,
} from '@leafwell/components';
import Image from 'next/image';
import Figure from './figure';
import HeadingBody from './heading-body';

const CardsListItem: React.FC<
  CardList &
    Pick<
      BlockCardsList,
      'listColor' | 'listNumbered' | 'contentAlignment' | 'listStep' | 'divider'
    > & {
      listTitleSize: string;
      index: number;
      isMinimal?: boolean;
      directionRow?: boolean;
    } & {
      isSecondary?: boolean;
      isLast?: boolean;
    }
> = ({
  title,
  content,
  image,
  fullWidthImage = false,
  index,
  listColor = 'medium',
  listNumbered = false,
  listStep = false,
  divider = false,
  isMinimal = true,
  directionRow = false,
  isSecondary = false,
  contentAlignment = 'top',
  listTitleSize,
  isLast = false,
}) => {
  const noBackgroundColor = listColor === 'none';
  const alignTop = contentAlignment === 'top';

  const cardClassNames = useMemo(() => {
    if (isMinimal && alignTop) {
      return 'content-start';
    } else if (isMinimal && !alignTop) {
      return 'content-between';
    } else if (directionRow) {
      if (isSecondary) {
        return 'h-32 md:h-56 lg:h-72 grid gap-4 md:content-end';
      } else {
        return 'min-h-56 lg:h-96 grid-rows-card-medium !gap-0';
      }
    } else {
      return 'lg:content-between';
    }
  }, [isMinimal, directionRow, isSecondary]);
  return (
    <Card
      filled={!isMinimal && !noBackgroundColor}
      rounded={!isMinimal}
      size="medium"
      tagName="li"
      variant={
        isMinimal
          ? 'minimal'
          : noBackgroundColor
          ? 'light'
          : (listColor as CardProps['variant'])
      }
      className={[
        cardClassNames,
        fullWidthImage ? 'h-96 relative !p-0' : '',
        divider && !isLast ? 'border-e-lilac-100' : '',
        divider ? 'md:p-4' : '',
        isMinimal ? 'grid-cols-8' : '',
      ].join(' ')}
    >
      {listNumbered && (
        <Paragraph
          size="small"
          className={[
            isMinimal
              ? 'col-span-1 text-center inline-grid items-center w-7 h-7 rounded-full bg-lilac-300'
              : directionRow
              ? '!text-2xl'
              : '',
          ].join(' ')}
        >
          {isMinimal ? index + 1 : ('0' + (index + 1)).slice(-2)}
        </Paragraph>
      )}
      {listStep && (
        <Badge
          className="w-fit col-span-full"
          label={`Step ${
            isMinimal ? index + 1 : ('0' + (index + 1)).slice(-2)
          }`}
          variant="default"
          size="xsmall"
        />
      )}
      <div
        className={[
          'grid',
          fullWidthImage ? 'h-inherit' : 'gap-6 grid-cols-inline-form',
          isMinimal ? 'col-span-7' : '',
        ].join(' ')}
      >
        {title && (
          <HeadingBody
            level={isMinimal ? '4' : '5'}
            title={title}
            className={[
              fullWidthImage ? 'p-6' : '',
              listTitleSize === 'small' ? '!text-base font-medium' : '',
            ].join(' ')}
            tagName="h3"
          />
        )}
        {image && !isSecondary && (
          <Figure
            {...{
              ...image,
              className: [
                fullWidthImage
                  ? 'place-self-end rounded-b-2xl w-full mb-0.5'
                  : title
                  ? 'w-8'
                  : 'w-fit',
                title ? '' : 'justify-self-center	mt-20 mb-20 lg:mb-0',
              ].join(' '),
            }}
          />
        )}
      </div>
      {content?.length > 0 ? (
        isSecondary ? (
          <div className="grid gap-4 h-auto md:h-40 lg:h-52">
            {image && <Figure {...{ ...image, className: 'w-8 md:w-16' }} />}
            <Paragraph
              dangerouslySetInnerHTML={{ __html: content }}
              size={listTitleSize === 'small' ? 'medium' : 'small'}
              className={[
                'mb-2',
                directionRow ? 'md:place-self-end' : '',
                isMinimal ? 'col-span-7 col-start-2' : '',
              ].join(' ')}
              tagName="div"
            />
          </div>
        ) : (
          <Paragraph
            dangerouslySetInnerHTML={{ __html: content }}
            size={listTitleSize === 'small' ? 'medium' : 'small'}
            className={[
              'mb-2',
              directionRow ? 'place-self-end' : '',
              isMinimal ? 'col-span-7' : '',
              isMinimal && listNumbered ? 'col-start-2' : '',
            ].join(' ')}
            tagName="div"
          />
        )
      ) : null}
    </Card>
  );
};

type CardsListStyles = {
  wrap: string;
  header: string;
  list: string;
  image: string;
};

const CardsListSecondary: React.FC<BlockCardsList> = ({
  sectionId,
  direction,
  content,
  image,
  list,
  listVariant = 'minimal',
  subTitle,
  title,
  titleSize,
  listTitleSize,
  ...listProps
}) => {
  const isMinimal = listVariant === 'minimal';
  const directionRow = direction === 'row';

  return (
    <Wrap id={sectionId}>
      <div className="grid gap-y-8 lg:grid-cols-2">
        <div className="grid grid-cols-2 lg:grid-cols-none gap-y-6">
          {title?.length > 0 && (
            <HeadingBody
              level={titleSize === 'big' ? '4' : '6'}
              title={title}
            />
          )}
          {image && (
            <Figure
              {...image}
              className="hidden md:block md:w-28 lg:ml-16 lg:w-64"
            />
          )}
          {content && (
            <Paragraph
              dangerouslySetInnerHTML={{ __html: content }}
              size="large"
            />
          )}
        </div>
        <div className="grid gap-16">
          {subTitle?.length > 0 && (
            <Paragraph
              size="large"
              className="lg:text-2xl lg:leading-tight lg:my-reset"
            >
              {subTitle}
            </Paragraph>
          )}

          <ol className="grid md:grid-cols-3 gap-6">
            {list.map((item, index) => (
              <CardsListItem
                {...item}
                {...listProps}
                key={index}
                index={index}
                isMinimal={isMinimal}
                listTitleSize={listTitleSize}
                directionRow={directionRow}
                isSecondary={true}
              />
            ))}
          </ol>
        </div>
      </div>
    </Wrap>
  );
};

const CardsListPrimary: React.FC<BlockCardsList> = ({
  sectionId,
  direction,
  button,
  content,
  image,
  list,
  listColumns = 3,
  listVariant = 'minimal',
  subTitle,
  subTitleSize,
  title,
  icon,
  titleAlignment,
  titleSize,
  ...listProps
}) => {
  const hasSubtitle = subTitle?.length > 0;
  const isMinimal = listVariant === 'minimal';
  const directionRow = direction === 'row';
  const imageFull =
    list.find(item => item.fullWidthImage === true) !== undefined;
  const classNames = useMemo(() => {
    // list needs to grow to make space for extra columns
    const listColumnSpan = 6 + (listColumns - 1);

    const styles = {
      wrap: {
        default: 'lg:grid-cols-12 lg:gap-x-12 xl:gap-x-24 lg:gap-y-6',
        row: '',
        column: 'items-start',
      },
      header: {
        default: 'grid gap-6 items-center lg:items-stretch justify-items-start',
        row: [
          'lg:col-span-full lg:mb-10',
          titleAlignment === 'center' ? '!justify-items-center' : '',
        ].join(' '),
        column: `col-span-12 lg:col-span-${12 - listColumnSpan}`,
      },
      list: {
        default: [
          'grid',
          !listProps.divider ? 'gap-8' : 'gap-8 md:gap-0',
          listColumns > 1 && !directionRow
            ? `sm:grid-cols-2 md:grid-cols-${listColumns}`
            : '',
          isMinimal && !listProps.divider ? 'lg:gap-10 xl:gap-x-16' : '',
        ].join(' '),
        row: [
          'lg:col-span-full',
          'md:grid-cols-2',
          `lg:grid-cols-${listColumns}`,
        ].join(' '),
        column: [`col-span-12 lg:col-span-${listColumnSpan}`].join(' '),
      },
      image: {
        default: '',
        row: 'hidden lg:block lg:w-108 lg:absolute lg:-top-36 lg:left-168',
        column: '',
      },
    };

    return Object.keys(styles).reduce(
      (result: CardsListStyles, key) => {
        result[key] = `${result[key]} ${styles[key][direction]}`;

        return result;
      },
      {
        wrap: styles.wrap.default,
        header: styles.header.default,
        list: styles.list.default,
        image: styles.list.default,
      },
    );
  }, [direction, listColumns, isMinimal]);

  return (
    <Wrap id={sectionId} className={classNames.wrap}>
      {hasSubtitle && (
        <Image
          src={listColumns > 1 ? '/diamond.svg' : '/diamond-green.svg'}
          className={[
            'col-span-full w-8',
            titleAlignment === 'center' ? 'justify-self-center' : '',
          ].join(' ')}
          loading="lazy"
          alt="diamond icon"
          height={40}
          width={39}
        />
      )}
      {icon && <Figure {...icon} className="col-span-full w-8" />}
      {(title?.length > 0 || hasSubtitle) && (
        <header
          className={[classNames.header, image ? 'relative' : ''].join(' ')}
        >
          <div
            className={[
              'grid gap-3 lg:gap-6 content-start',
              titleAlignment === 'center'
                ? 'w-60 sm:w-80 md:w-120 text-center'
                : '',
            ].join(' ')}
          >
            {hasSubtitle && (
              <Paragraph
                size="large"
                className={[
                  subTitleSize === 'small'
                    ? 'font-medium'
                    : 'text-2xl sm:text-3xl leading-tight',
                ].join(' ')}
              >
                {subTitle}
              </Paragraph>
            )}
            {title?.length > 0 && (
              <HeadingBody
                level={hasSubtitle || titleSize !== 'big' ? '4' : '2'}
                title={title}
              />
            )}
            {content && (
              <Paragraph
                dangerouslySetInnerHTML={{ __html: content }}
                size="large"
              />
            )}
            {image && <Figure {...image} className={classNames.image} />}
          </div>
          {button && !isMinimal && (
            <Button
              href={button.url}
              label={button.title}
              rounded={true}
              variant="gradient"
              target={button.target}
              className="sm:justify-self-end lg:justify-self-start self-end"
            />
          )}
        </header>
      )}
      <ol
        className={[
          classNames.list,
          imageFull ? 'justify-items-center' : '',
        ].join(' ')}
      >
        {list.map((item, index) => {
          const isLast = index === list?.length - 1;
          return (
            <CardsListItem
              {...item}
              {...listProps}
              key={index}
              index={index}
              isMinimal={isMinimal}
              directionRow={directionRow}
              isLast={isLast}
              listTitleSize={titleSize}
            />
          );
        })}
      </ol>
      {button && isMinimal && (
        <Button
          href={button.url}
          label={button.title}
          rounded={true}
          variant="secondary"
          target={button.target}
          className="w-fit h-fit self-start col-span-full lg:justify-self-center -mt-4 md:mt-0"
        />
      )}
    </Wrap>
  );
};

const CardsList: React.FC<BlockCardsList> = ({
  titleLayout = 'default',
  ...cardsListProps
}) => {
  // isDefault means the title and label are displayed in a column. Otherwise they will be displayed side by side
  const isDefault = titleLayout === 'default';
  return isDefault ? (
    <CardsListPrimary titleLayout={titleLayout} {...cardsListProps} />
  ) : (
    <CardsListSecondary titleLayout={titleLayout} {...cardsListProps} />
  );
};

export default CardsList;
